/**
 * AcDbAssocArray OBJECTS-section positional sentinel (docs/MASTERPLAN.md N9,
 * AW-0-OBJECTS-BUCKET-ORDER — the byte-position residual AW-0 explicitly
 * deferred, see the AW-0 foundation comment in
 * `drawing-model/src/dxfBridge.import.ts`).
 *
 * Mirrors `SPATIAL_FILTER_PLACEHOLDER_CODE` / `SPATIAL_FILTER_PLACEHOLDER_
 * INDEX_PREFIX` (`objects/spatialFilterTypes.ts`) exactly, and — like that
 * module — is extracted into its own tiny file rather than living inline in
 * `types.ts` / `assocArrayParser.ts`: it keeps `types.ts` under the
 * CLAUDE.md 2000-line file-size ceiling, and avoids a
 * `types.ts` ↔ `sectionParsers.ts` ↔ `writer.ts` import tangle (both of those
 * need the sentinel constant; `types.ts` does not need to know about it at
 * all — `DxfObjectsSection.assocArrays` only references the unrelated
 * `DxfAssocArrayObject` type).
 *
 * `parseObjectsSection` diverts every AcDbAssocArray graph record
 * (ACDBASSOCNETWORK / ACDBASSOCACTION / ACDBASSOCARRAYACTIONBODY /
 * ACDBASSOCARRAY*PARAMETERS / ACDBASSOCDEPENDENCY) into the typed
 * `assocArrays[]` bucket but ALSO pushes a single
 * `{ code: ASSOC_ARRAY_PLACEHOLDER_CODE, value: <handle | #idx:N> }` group
 * into `rawObjectGroups` AT THAT POSITION, so a graph interleaved among
 * unsupported OBJECTS records keeps its relative position instead of being
 * hoisted (or, pre-N9, re-appended after the whole catch-all).
 * `serializeObjectsSection` (dxf-io writer) and `buildBridgeRawObjectGroups`
 * (drawing-model import bridge) both substitute the placeholder with the
 * re-synthesised record's groups.
 */

/**
 * Private sentinel group code.
 *
 * @codex local review (N9 round-3) — `-2` (the original value here) is NOT
 * safe: the Autodesk DXF reference's "Group Codes in Numerical Order"
 * table reserves the small negative range (`-1` … roughly `-5`) for
 * `APP:`-prefixed pseudo-codes (`-1` entity name, `-2` entity name /
 * multiple, `-3` XDATA sentinel, `-4` `ssget` filter operator, `-5`
 * persistent reactor chain). Autodesk's own docs say these are "not saved
 * in a DXF file" by a CONFORMANT producer, but this parser does not
 * validate that every raw group code is non-negative before accepting it
 * (by design — it must tolerate malformed / vendor / hand-built input
 * without throwing), so a `-2` group genuinely present in such input was
 * indistinguishable from this sentinel — silent data loss on export
 * (verified: `0/VENDOR, 5/A1, -2/REAL_NEGATIVE_PAYLOAD` round-tripped
 * through `parseDxfDocument` → `serializeDxfDocument` dropped the payload).
 *
 * `-1000000` has no meaning anywhere in the Autodesk DXF reference (neither
 * the persisted 0–1071-ish / 1000+ XDATA / 100000+ object-specific ranges,
 * NOR the small `APP:`-only negative cluster above) — no real or malformed
 * DXF group code can plausibly collide with it. `SPATIAL_FILTER_
 * PLACEHOLDER_CODE` (`-1`, `objects/spatialFilterTypes.ts`) shares the SAME
 * theoretical exposure (`-1` IS the literal `APP: entity name` code) — a
 * pre-existing, already-shipped choice out of this slice's scope to change;
 * only this NEW sentinel is fixed here.
 *
 * @codex local review (N9 round 5 → 6, resolved) — round 5 additionally
 * changed the WRITER/BRIDGE substitution loops so an UNRESOLVABLE match
 * (code matches but `value` does not resolve to a real, present
 * `assocArrays[]` entry) passed through verbatim instead of being dropped,
 * reasoning that no finite magic value is EVER perfectly collision-proof.
 * Round 6 found that broke a DIFFERENT, more important guarantee the
 * SPATIAL_FILTER precedent documents explicitly: a caller filtering /
 * removing an entry from `assocArrays[]` after parsing is a SUPPORTED
 * scenario (handle-based lookup exists specifically so a consumer CAN
 * reorder/filter the typed array), and the resulting orphaned sentinel MUST
 * be dropped, not leaked as a corrupted `-1000000` group. Distinguishing
 * "genuinely orphaned sentinel" from "coincidental unrelated data" by
 * construction would need out-of-band ordinal tracking (a real fix, but a
 * materially bigger change, and a unilateral divergence from the
 * SPATIAL_FILTER precedent this whole slice is chartered to MIRROR, not
 * improve on independently). Reverted to DROP-on-unresolved — 1:1 parity
 * with `SPATIAL_FILTER_PLACEHOLDER_CODE`'s already-shipped, already-accepted
 * behaviour. `-1000000`'s magnitude (this doc, above) is what keeps the
 * residual "a malformed file literally carries this exact code" risk
 * vanishingly small WITHOUT needing a pass-through escape hatch at all.
 */
export const ASSOC_ARRAY_PLACEHOLDER_CODE = -1000000;

/**
 * Discriminator prefix for the rare degenerate case where an AcDbAssocArray
 * graph record carries no handle (DXF group 5 — malformed / hand-built).
 * Mirrors `SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX` exactly: the sentinel
 * normally stores the record's STABLE handle so the writer/bridge resolves
 * the right record even if `assocArrays[]` is later reordered or filtered; a
 * handle-less record falls back to its positional index, stored as
 * `${PREFIX}${index}`. The prefix begins with `#`, which is NOT a hex digit,
 * so it can never collide with a real handle.
 */
export const ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX = "#idx:";
